import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Flex,
  Text,
  Spinner,
  useDisclosure,
  useToast
} from '@chakra-ui/react'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'

import useMrpPurchase from '@hook/mrp/use-purchase'

import TableGrid from '@component/Layout/TableGrid'

import suppliers from '@helper/suppliers'
import generic from '@service/generic'
import mrp from '@service/mrp'

import { ILayoutActionSuppliers } from '@base/@types/components/Layout/Tools'
import mrpPurchaseSuppliers from '@base/variables/headers/suppliers/purchase'

import LayoutToolsButton from '../Components/Button'

const ActionPurchaseSuppliers: FC<ILayoutActionSuppliers> = ({
  data,
  onUpdate,
  isScheduled = false
}) => {
  const toast = useToast()

  const { isOpen, onOpen, onClose } = useDisclosure()
  const { handleUpdateData, params, handleLoading } = useMrpPurchase()

  const headers = useMemo(() => mrpPurchaseSuppliers, [])

  const [rows, updateRows] = useImmer<any[]>([])

  const [loading, setLoadingSupply] = useState(true)
  const [error, setError] = useState(false)
  const [completting, setCompletting] = useState(false)

  const getData = useCallback(async () => {
    setLoadingSupply(true)
    setError(false)

    try {
      const {
        data: { data: res }
      } = await mrp.getSuppliersResume(suppliers.parseParams(data), isScheduled)

      updateRows(draft => {
        return draft
          .filter((row: any) => row.id === 'null')
          .concat(suppliers.parseResult(res, isScheduled))
      })
    } catch (ex) {
      setError(true)
    }

    setLoadingSupply(false)
  }, [data, isScheduled, updateRows])

  const complete = async () => {
    setCompletting(true)
    handleLoading()

    try {
      const {
        data: { data: res }
      } = await generic.completeMinimumOrderSupplier(data)

      onUpdate((draft: any) => {
        return draft.filter((row: any) => row.id === 'null').concat(res)
      })

      handleUpdateData(res)

      toast({
        title: 'Sucesso!',
        description: 'Os mínimos dos fornecedores foram completados',
        status: 'success',
        position: 'top'
      })
    } catch {
      toast({
        title: 'Opss...',
        description:
          'Os mínimos dos fornecedores não foram completados. Verificar se há minimo cadastrado',
        status: 'error',
        position: 'top'
      })
    }

    setCompletting(false)
  }

  useEffect(() => {
    if (isOpen && data.length > 0) {
      getData()
    }
  }, [getData, data, isOpen])

  return (
    <>
      <LayoutToolsButton label="Pedido mínimo" onClick={onOpen} />
      <Modal isOpen={isOpen} onClose={onClose} size="3xl">
        <ModalOverlay />
        <ModalContent transition="all ease .3s">
          <ModalHeader bgColor="white">Fornecedores</ModalHeader>
          <ModalCloseButton />
          <ModalBody bgColor="white">
            {error ? (
              <Flex
                flexDir="column"
                alignItems="center"
                justifyContent="center"
                h="380px"
              >
                <Text mb={4}>Ocorreu um erro ao tentar recuperar os dados</Text>
                <Button onClick={getData}>Tentar novamente</Button>
              </Flex>
            ) : loading ? (
              <Flex alignItems="center" justifyContent="center" h="380px">
                <Spinner />
              </Flex>
            ) : (
              <TableGrid
                id="table-suppliers"
                headers={headers}
                data={rows}
                height="360px"
              />
            )}
          </ModalBody>
          <ModalFooter justifyContent="center" bgColor="white">
            {!isScheduled && params.fornecedoresIds.length === 1 && (
              <Button
                variant="ghost"
                size="sm"
                borderColor="blackAlpha.400"
                _hover={{ bg: 'gray.400' }}
                isLoading={completting}
                isDisabled={completting || loading}
                onClick={() => {
                  complete()
                  onClose()
                }}
                color="blackAlpha.700"
              >
                Completar valor mínimo de todos os pedidos
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default ActionPurchaseSuppliers
